import React, { useCallback, useEffect, useState } from "react";
import { Accordion, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { ErrorMessage, Formik } from "formik";
import moment from "moment";
import * as Yup from "yup";
import swal from "sweetalert";

import AlertError from "@components/shared/alert-error";
import { useCommentValidation } from "@contexts/comment-validation-context";
import { getConciergeCalls, getConciergeCatalogs, saveConciergeCall } from "@services/concierge-calls";
import { useBinnaclesStore } from "@stores/binnacles";
import { getInputClasses } from "@utils/get-input-classes";

const REQUIRED = "El campo es requerido";
const EMPTY_CATALOGS = { callTypes: [], responseTypes: [], responders: [], contactMedia: [] };

const INITIAL_VALUES = { callType: "", responseType: "", responder: "", contactMedium: "", comments: "" };

const validationSchema = Yup.object().shape({
  callType: Yup.string().required(REQUIRED),
  responseType: Yup.string().required(REQUIRED),
  contactMedium: Yup.string().required(REQUIRED),
  comments: Yup.string().trim().required(REQUIRED).max(500, "Maximo 500 caracteres"),
});

const getErrorMessage = (error) => {
  const data = error && error.response && error.response.data;
  return typeof data === "string" && data ? data : "No se pudo guardar la llamada. Intenta de nuevo.";
};

export const ConciergeFollowUp = (props) => (
  <Card>
    <Card.Header>
      <Accordion.Toggle as={Button} variant="link" eventKey={props.eventKey}>
        Seguimiento Concierge
      </Accordion.Toggle>
      <i
        className={`fa ${props.icon}`}
        style={{ position: "absolute", right: "0", marginRight: "15px", marginTop: "10px" }}
      />
    </Card.Header>
    <Accordion.Collapse eventKey={props.eventKey}>
      <Card.Body>
        <ContentConciergeFollowUp {...props} />
      </Card.Body>
    </Accordion.Collapse>
  </Card>
);

const CatalogSelect = ({ label, name, options, required, formik }) => (
  <Form.Group controlId={name}>
    <Form.Label>
      {label}
      {required ? " *" : ""}
    </Form.Label>
    <Form.Control
      as="select"
      name={name}
      value={formik.values[name]}
      onChange={formik.handleChange}
      className={getInputClasses(formik.touched, formik.errors, name)}
    >
      <option value="">Seleccionar...</option>
      {options.map((option) => (
        <option key={option.code} value={option.code}>
          {option.label}
        </option>
      ))}
    </Form.Control>
    <ErrorMessage name={name}>
      {(message) => <Form.Control.Feedback type="invalid">{message}</Form.Control.Feedback>}
    </ErrorMessage>
  </Form.Group>
);

/**
 * Registro de llamadas de seguimiento al asegurado. Cada llamada se guarda en su propia tabla y su
 * comentario queda en la bitacora como sustento del seguimiento; aqui se listan las anteriores.
 */
export const ContentConciergeFollowUp = ({ data }) => {
  const caseId = data && data.id;
  const [catalogs, setCatalogs] = useState(EMPTY_CATALOGS);
  const [calls, setCalls] = useState([]);
  const { markAsCommented } = useCommentValidation();
  const { fetchAllByCase } = useBinnaclesStore();

  const loadCalls = useCallback(
    () => getConciergeCalls(caseId).then((response) => setCalls(response.data)).catch(() => {}),
    [caseId]
  );

  useEffect(() => {
    getConciergeCatalogs().then((response) => setCatalogs(response.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (caseId) {
      loadCalls();
    }
  }, [caseId, loadCalls]);

  const onSubmit = (values, { resetForm, setStatus, setSubmitting }) => {
    setStatus(null);
    saveConciergeCall(caseId, { ...values, comments: values.comments.trim() })
      .then(() => {
        resetForm();
        // El comentario ya quedo en la bitacora: cuenta como el comentario que piden las acciones del caso.
        markAsCommented();
        fetchAllByCase(caseId).catch(() => {});
        swal("Guardado Exitoso", "La llamada quedó registrada en el seguimiento y en la bitácora", "success");
        return loadCalls();
      })
      .catch((error) => setStatus(getErrorMessage(error)))
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <h6 className="text-primary mb-3">
        <i className="fa fa-phone mr-2" />
        Llamada
      </h6>

      <div style={{ background: "#eef5ff", borderRadius: "8px", padding: "16px" }} className="mb-4">
        <div className="font-weight-bold text-primary mb-3">
          <i className="fa fa-phone mr-2" />
          Registro de llamada
        </div>
        <Formik initialValues={INITIAL_VALUES} validationSchema={validationSchema} onSubmit={onSubmit}>
          {(formik) => (
            <Form onSubmit={formik.handleSubmit} noValidate>
              <AlertError status={formik.status} />
              <Row>
                <Col md={6}>
                  <CatalogSelect label="Tipo de Llamada" name="callType" options={catalogs.callTypes} required formik={formik} />
                </Col>
                <Col md={6}>
                  <CatalogSelect label="Tipo de Respuesta" name="responseType" options={catalogs.responseTypes} required formik={formik} />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <CatalogSelect label="¿Quién respondió?" name="responder" options={catalogs.responders} formik={formik} />
                </Col>
                <Col md={6}>
                  <CatalogSelect label="Medio de Contacto" name="contactMedium" options={catalogs.contactMedia} required formik={formik} />
                </Col>
              </Row>
              <Form.Group controlId="comments">
                <Form.Label>Comentarios *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  maxLength={500}
                  name="comments"
                  placeholder="Registra los comentarios correspondientes a la llamada con el asegurado"
                  value={formik.values.comments}
                  onChange={formik.handleChange}
                  className={getInputClasses(formik.touched, formik.errors, "comments")}
                />
                <ErrorMessage name="comments">
                  {(message) => <Form.Control.Feedback type="invalid">{message}</Form.Control.Feedback>}
                </ErrorMessage>
                <Form.Text className="text-muted">
                  El comentario se registrará en la bitácora como sustento del seguimiento realizado por Concierge.
                </Form.Text>
              </Form.Group>
              <div className="text-right">
                <Button type="submit" variant="primary" disabled={formik.isSubmitting || !caseId}>
                  {formik.isSubmitting ? (
                    <Spinner as="span" animation="border" size="sm" className="mr-2" />
                  ) : (
                    <i className="far fa-check-circle mr-2" />
                  )}
                  Guardar
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <h6 className="mb-2">Llamadas registradas</h6>
      {calls.length === 0 ? (
        <div className="text-muted small">Aún no hay llamadas registradas.</div>
      ) : (
        <Table responsive size="sm" striped>
          <thead>
            <tr>
              <th>Fecha y hora</th>
              <th>Tipo</th>
              <th>Respuesta</th>
              <th>Respondió</th>
              <th>Medio</th>
              <th>Comentarios</th>
              <th>Usuario</th>
            </tr>
          </thead>
          <tbody>
            {calls.map((call) => (
              <tr key={call.id}>
                <td>{call.createdAt ? moment(call.createdAt).format("DD/MM/YYYY HH:mm") : ""}</td>
                <td>{call.callTypeLabel}</td>
                <td>{call.responseTypeLabel}</td>
                <td>{call.responderLabel || "—"}</td>
                <td>{call.contactMediumLabel}</td>
                <td>{call.comments}</td>
                <td>{call.userName}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
};
